import { html, css, LitElement, unsafeCSS } from 'lit';
import type { HTMLAttributes, DetailedHTMLProps } from 'react';
import { declareComponent } from './declareComponent';

export interface WikiGridHTMLAttributes<T> extends HTMLAttributes<T> {
   column: number | null;
}

declare global {
   // eslint-disable-next-line @typescript-eslint/no-namespace
   namespace JSX {
      interface IntrinsicElements {
         'wiki-grid': DetailedHTMLProps<WikiGridHTMLAttributes<WikiGrid>, WikiGrid>;
      }
   }
}

class WikiGrid extends LitElement {
   static styles = css`
      :host {
         container-type: inline-size;
         display: block;
         font: inherit;
      }

      .grid-box {
         display: grid;
         grid-template-columns: repeat(var(--columns), minmax(var(--required-width), 1fr));
         align-items: center;
         justify-items: center;
         align-content: space-between;
         justify-content: space-between;
         gap: 0.5em;
      }

      .flex-box {
         width: 100%;
         display: grid;
         grid-template-columns: repeat(auto-fit, minmax(var(--required-width), max-content));
         justify-content: center;
         align-items: end;
         justify-items: center;
         gap: 10px;
      }
   `;

   static properties = { columns: { type: Number }, requiredWidth: { type: Number } };
   declare columns: number | null;
   declare requiredWidth: number;

   constructor() {
      super();
      this.columns = null;
      this.requiredWidth = 100;
   }

   /**
    * We use this to add light-DOM styles which are scoped to this instance
    * of the component. That allows us to style the (slotted) children.
    *
    * This should still be scoped and well-behaved, because the random selector ensures that
    * all those light-DOM styles can only target this specific instance.
    */
   connectedCallback(): void {
      super.connectedCallback();

      // generate a unique class for this instance
      const selector = unsafeCSS(`wiki-grid-${Math.random().toString(36).substring(2, 15)}`);
      const style = document.createElement('style');
      this.append(style);

      this.classList.add(selector.cssText);
      /*
       * NOTE: The CSS in the GridEditor component should be similar
       * to this CSS here, and should be updated as-needed to keep
       * the WYSIWYG behavior.
       */
      const styleContent = css`
         wiki-grid.${selector} {
            .imageBox:has(p) {
               display: grid;
               grid-template-rows: subgrid;
               img {
                  margin: 0;
                  max-height: 100%;
                  width: 100%;
                  object-fit: contain;
                  align-self: end;
                  object-position: bottom;
               }
               p {
                  margin: 0;
                  text-align: center;
               }
            }
            > * {
               margin-inline: 0 !important;
               grid-row-end: span 2;
            }
         }
      `;

      style.textContent = styleContent.cssText;
   }

   render() {
      const minColumnFitWidth = (this.columns || 1) * this.requiredWidth;
      return html`
         <style>
            :host {
               --columns: ${this.columns};
               --required-width: ${this.requiredWidth}px;
            }

            /* We can't use CSS variables in media queries, so this has to be computed in JS */
            @media (max-width: ${minColumnFitWidth}px) {
               .grid-box {
                  --columns: auto-fit;
               }
            }

            @container (width < ${minColumnFitWidth}px) {
               .grid-box {
                  --columns: auto-fit;
               }
            }
         </style>
         <div class="${this.columns === null ? 'flex-box' : 'grid-box'}">
            <slot></slot>
         </div>
      `;
   }
}

declareComponent('wiki-grid', WikiGrid);
